import { api } from '../../api.ts';
import { agentCounts, agentsApi, byUrgency } from '../../agents-api.ts';
import type { AgentInfo, ProjectInfo } from '../../agents-api.ts';
import { h } from '../../ui.ts';
import { icon, itemCard, screen } from './kit.ts';
import type { TalkSession, WorkItem } from './kit.ts';
import { chosenProject } from './switcher.ts';
import { agentCard } from './talk-screen.ts';

/**
 * The chat (design-docs/0032): typed instead of spoken, on the phone and the desktop. The lines so far, what she
 * started from them, and a composer. Her answers and the loops' reports arrive as `channel.message`.
 */

interface JournalRow {
  at: number;
  event: string;
  payload: { speaker?: string; text?: string; channel?: string };
}
type Full = TalkSession & { journal: JournalRow[] };

/** the owner's open chat about the chosen project, or a new one */
export async function chatView(): Promise<HTMLElement> {
  const projects = await agentsApi.projects().catch(() => [] as ProjectInfo[]);
  const project = chosenProject(projects);
  const s = await api.post<TalkSession>('/chat', project ? { project } : {});
  const about = projects.find((p) => p.slug === project)?.name ?? null;
  const chat = new Chat(s);
  await chat.load();
  chat.follow();
  const end = async () => {
    await api.post(`/sessions/${s.id}/end`).catch(() => {});
    location.hash = '#/';
  };
  return screen(
    h(
      'header',
      { class: 'p-head' },
      h('a', { class: 'p-icon', href: '#/', 'aria-label': 'Home (the chat keeps going)' }, icon.down()),
      h('span', { class: 'p-pill' }, h('span', { class: 'p-live on' }), 'CHAT'),
      h('button', { class: 'p-icon', 'aria-label': 'End chat', onclick: () => void end() }, icon.stop(18)),
    ),
    about ? h('div', { class: 'p-about' }, icon.folder(12), about) : null,
    chat.thread,
    chat.cards,
    h('div', { class: 'p-grow' }),
    composer((text) => chat.send(text)),
  );
}

/** One line of the chat: yours, or hers (an answer, or what a loop started). */
function bubble(who: 'you' | 'her', text: string, pending = false): HTMLElement {
  return h('div', { class: `p-msg ${who} ${pending ? 'pending' : ''}` }, h('span', {}, text));
}

class Chat {
  readonly thread = h('section', { class: 'p-thread', 'aria-label': 'Chat', 'aria-live': 'polite' });
  readonly cards = h('section', { class: 'p-cards p-talk-cards', 'aria-label': 'What she started and filed' });
  /** lines shown as soon as they are sent; their `transcript.segment` events are skipped when they arrive */
  private sent = 0;
  private waiting: HTMLElement | null = null;

  constructor(readonly s: TalkSession) {}

  /** The lines so far from the journal, so a reopened chat shows the whole conversation. */
  async load() {
    const full = await api.get<Full>(`/sessions/${this.s.id}`);
    for (const j of full.journal) {
      if (j.event === 'transcript.segment' && j.payload.speaker !== 'assistant') this.add('you', j.payload.text ?? '');
      if (j.event === 'channel.message' && j.payload.channel === 'dashboard') this.add('her', j.payload.text ?? '');
    }
    if (!full.journal.some((j) => j.event === 'transcript.segment'))
      this.thread.append(
        h('p', { class: 'p-empty' }, 'Ask anything, or say what should change. She answers here and starts the work.'),
      );
    await this.refresh();
  }

  follow() {
    const off = api.events((name, payload) => {
      const p = payload as { sessionId?: string; text?: string; speaker?: string; channel?: string };
      if (p.sessionId !== this.s.id) return;
      if (name === 'transcript.segment' && p.speaker !== 'assistant') {
        if (this.sent > 0) this.sent -= 1;
        else this.add('you', p.text ?? '');
      }
      if (name === 'channel.message' && p.channel === 'dashboard') {
        this.add('her', p.text ?? '');
        void this.refresh();
      }
      if (name === 'session.ended') {
        off();
        location.hash = '#/';
      }
    });
    window.addEventListener('hashchange', off, { once: true });
  }

  /** The line goes on the screen at once; she thinks after the request returns. */
  async send(text: string) {
    this.sent += 1;
    this.add('you', text);
    this.waiting = bubble('her', '…', true);
    this.thread.append(this.waiting);
    this.scroll();
    try {
      await api.post(`/chat/${this.s.id}/say`, { text });
    } catch (err) {
      this.sent -= 1;
      this.waiting?.remove();
      this.waiting = null;
      throw err;
    }
  }

  private add(who: 'you' | 'her', text: string) {
    this.thread.querySelector('.p-empty')?.remove();
    if (who === 'her' && this.waiting) {
      this.waiting.remove();
      this.waiting = null;
    }
    this.thread.append(bubble(who, text));
    this.scroll();
  }

  private scroll() {
    this.thread.lastElementChild?.scrollIntoView({ block: 'nearest' });
  }

  /** What this chat filed and the agents it started (the same cards as a talk). */
  private async refresh() {
    const [work, agents] = await Promise.all([
      api.get<{ items: WorkItem[] }>(`/sessions/${this.s.id}/work`).catch(() => null),
      agentsApi.list().catch(() => [] as AgentInfo[]),
    ]);
    const mine = agents.filter((a) => a.talkSessionId === this.s.id).sort(byUrgency);
    const items = work?.items ?? [];
    this.cards.replaceChildren(
      ...mine.slice(0, 3).map(agentCard),
      ...items.slice().reverse().slice(0, 3).map(itemCard),
    );
    const c = agentCounts(mine);
    this.cards.classList.toggle('amber', c.asking > 0);
  }
}

/** The composer: Enter sends, Shift+Enter breaks the line. */
function composer(send: (text: string) => Promise<void>): HTMLElement {
  const input = h('textarea', {
    class: 'p-compose-input',
    rows: '1',
    placeholder: 'Write to her…',
    'aria-label': 'Your message',
    autocomplete: 'off',
  });
  const button = h('button', { class: 'p-compose-send', 'aria-label': 'Send', type: 'button' }, icon.send(18));
  const go = async () => {
    const text = input.value.trim();
    if (!text) return;
    input.value = '';
    input.style.height = '';
    button.setAttribute('disabled', '');
    try {
      await send(text);
    } catch (err) {
      input.value = text;
      input.placeholder = err instanceof Error ? err.message : String(err);
    } finally {
      button.removeAttribute('disabled');
      input.focus();
    }
  };
  button.addEventListener('click', () => void go());
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void go();
    }
  });
  input.addEventListener('input', () => {
    input.style.height = '';
    input.style.height = `${Math.min(input.scrollHeight, 140)}px`;
  });
  return h('div', { class: 'p-foot p-compose' }, input, button);
}
